/* eslint-disable @typescript-eslint/no-unused-vars */
/* for mobx decorators? */
import '@progress/kendo-theme-material/dist/all.css';

import { Chat as ChatUI, Message, User } from '@progress/kendo-react-conversational-ui';
import cuid from 'cuid';
import { withTheme } from 'emotion-theming';
import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';

import { BotResponseRating, ChannelStatus, CSATRating } from '../../enums/ChannelStatus';
import IChatOptions from '../../interfaces/IChatOptions';
import { clientRouterClients } from '../../interfaces/IRouterClient';
import { ChatAction, IAttachment } from '../../interfaces/ITwilioMessage';
import { logInfo } from '../../lib/logging';
import postMessage, { CrossFrameMessageType } from '../../lib/postMessage';
import { submitSurvey } from '../../lib/submitSurvey';
import ChatStore, { IChatListener } from '../../stores/ChatStore';
import IdentityStore from '../../stores/IdentityStore';
import CSATSurvey from '../CSATSurvey';
import Modal from '../Modal/Modal';
import ChatExit from './components/ChatExit/ChatExit';
import ChatHeader from './components/ChatHeader/ChatHeader';
import ChatMessage from './components/ChatMessage';
import ClientRouterModal from './components/ClientRouterModal/ClientRouterModal';
import CollectionForm from './components/CollectionForm/CollectionForm';
import MessageSection from './components/MessageSection/MessageSection';
import MessageSectionDisabled from './components/MessageSection/MessageSectionDisabled';
import SuggestedActions from './components/SuggestedActions/SuggestedActions';
import SuggestedProviders from './components/SuggestedProviders/SuggestedProviders';
import TimeSlotPicker from './components/TimeSlotPicker';
import { Button, ChatBody, Container, FlexContainer, ImageAttachment } from './styled';

export interface IChatAreaProps {
  options: IChatOptions;
  name?: string;
  isWidget?: boolean;
}

export interface IChatAreaState {
  user: User;
  showModal: boolean;
  isRouterClient: boolean;
  showClientRouterModal: boolean;
  routerChatKey?: string;
  enabled?: boolean;
  contentType?: string;
  showCSATSurvey: boolean;
  surveyError: null | string;
  isCustomerEndingChat: boolean;
}

export interface IInjectedProps extends IChatAreaProps {
  chatStore: ChatStore;
  identityStore: IdentityStore;
}

@inject('chatStore', 'identityStore')
@observer
class ChatArea extends Component<IChatAreaProps, IChatAreaState> {
  private messageListener: IChatListener | null = null;
  private actionListener: IChatListener | null = null;

  constructor(props: IChatAreaProps) {
    super(props);

    const { identityStore } = this.injected;
    if (identityStore.id === '' || props.options.userIdentity) {
      identityStore.id = props.options.userIdentity || `web_${cuid()}`;
    }

    const isRouterClient = clientRouterClients.includes(props.options.apiKey);
    const { channelSid } = this.injected.chatStore;
    const hasExistingChatChannel = !!channelSid;

    this.state = {
      isRouterClient,
      user: {
        id: identityStore.id,
        name: this.props.name,
      },
      showModal: false,
      showClientRouterModal: !hasExistingChatChannel && isRouterClient,
      enabled: true,
      showCSATSurvey: true,
      surveyError: null,
      isCustomerEndingChat: false,
    };
  }

  protected get injected(): IInjectedProps {
    return this.props as IInjectedProps;
  }

  public componentDidUpdate() {
    // get intention of last message to enable or disable chat area
    const [lastMessage] = this.injected.chatStore.messagesWithTyping.slice(-1);
    const willEnable = lastMessage
      ? lastMessage.attachments
        ? lastMessage.attachments[0]
          ? lastMessage.attachments[0].content
            ? lastMessage.attachments[0].content.inputEnabled
            : undefined
          : undefined
        : undefined
      : undefined;
    if (willEnable === undefined) {
      return;
    }
    // set state to last msg's intention if there is a mismatch with current state
    if (this.state.enabled !== willEnable) {
      this.setState({ enabled: willEnable });
    }
  }

  public async componentDidMount() {
    const { chatStore } = this.injected;
    if (this.props.options.env === 'widget-generator') {
      this.setGeneratorWidget();
      return;
    }

    if (!this.state.showClientRouterModal) {
      // do not start the chat if the apiKey requires a client router
      await chatStore.open(this.props.options, this.state.user);
    }
    this.messageListener = chatStore.onMessage((msg) => {
      logInfo(`MESSAGE: '${msg.text}' by ${msg.author.name}`);
    });
    this.actionListener = chatStore.onAction((action) => {
      logInfo(`ACTION: '${action.name}'`, action.payload);
    });
    window.addEventListener('focus', () => this.injected.chatStore.setMessagesConsumed());
  }

  public componentWillUnmount() {
    if (this.messageListener) {
      this.messageListener.off();
      this.messageListener = null;
    }
    if (this.actionListener) {
      this.actionListener.off();
      this.actionListener = null;
    }
    window.removeEventListener('focus', () => this.injected.chatStore.setMessagesConsumed());
  }

  public async initiateChatStore() {
    const { routerChatKey } = this.state;

    if (routerChatKey) {
      await this.injected.chatStore.open(
        {
          ...this.props.options,
          apiKey: routerChatKey,
        },
        this.state.user,
      );
    }
  }

  public async startChat() {
    await this.initiateChatStore();
    this.setState({ showClientRouterModal: false });
  }

  public setGeneratorWidget() {
    // initializes the chat area for the widget generator

    const { chatStore } = this.injected;
    const {
      options: { initialResponses },
    } = this.props;

    const attachments: IAttachment[] = [];

    if (initialResponses && initialResponses.length > 0) {
      attachments.push({
        contentType: ChatAction.SuggestAction,
        content: { suggestedActions: initialResponses },
        messageId: '-1',
      });
    }

    const initialMessage = {
      text: this.props.options.initialMessage,
      author: { id: '-1', name: '' },
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    const customerMessage = {
      text: 'this is a customer message',
      author: this.state.user,
      timestamp: new Date(),
    };

    chatStore.messages = [initialMessage, customerMessage];
  }

  public render() {
    const { primaryAccentColor } = this.props.options;
    const { user, showModal, showClientRouterModal } = this.state;
    const { status, messagesWithTyping } = this.injected.chatStore;
    const showMessageSectionDisabled =
      !this.state.enabled || (this.state.enabled && status === ChannelStatus.Ended) || this.state.isCustomerEndingChat;
    const showCSATSurvey =
      (status === ChannelStatus.Ended && this.state.showCSATSurvey) || this.state.isCustomerEndingChat;

    // scroll fix - allow chatui component to detect change
    let msgs: Message[] = [];
    if (messagesWithTyping) {
      msgs = [...messagesWithTyping];
    }

    this.scrollMessageList();

    return (
      <Container>
        <ChatHeader
          options={this.props.options}
          onClickEndChatHandler={() => this.setState({ showModal: !showModal })}
          onMinimizeHandler={this.minimize}
          userCanEndChat={ChannelStatus.Active === status ? true : false}
        />
        <ChatBody>
          <ChatUI
            user={user}
            messages={msgs}
            // onMessageSend={(message) => this.injected.chatStore.sendMessage(message)}
            placeholder='Type your message here'
            messageTemplate={ChatMessage}
            attachmentTemplate={(props: { item: IAttachment }) => {
              // render attachments based on attachment content type.
              const { contentType, content } = props.item;
              if (contentType === ChatAction.ShowTimeSlots || contentType === ChatAction.ShowTimeSlotsMedia) {
                return (
                  <TimeSlotPicker
                    data={props.item.content.timeSlotData}
                    timezone={props.item.content.timezone}
                    messageId={props.item.messageId}
                    scrollMessageList={this.scrollMessageList}
                  />
                );
              } else if (contentType === ChatAction.SuggestProviders) {
                return (
                  <SuggestedProviders
                    messageId={props.item.messageId}
                    providers={props.item.content.providers}
                    onActionClick={this.handleActionClick.bind(this)}
                  />
                );
              } else if (contentType === ChatAction.SuggestAction && status === ChannelStatus.Unknown) {
                return (
                  <SuggestedActions
                    messageId={props.item.messageId}
                    actions={props.item.content.suggestedActions}
                    onActionClick={this.handleActionClick.bind(this)}
                  />
                );
              } else if (contentType === ChatAction.GetPhone) {
                return (
                  <CollectionForm
                    messageId={props.item.messageId}
                    fields={props.item.content.collectedFields}
                    onActionClick={this.handleActionClick.bind(this)}
                  />
                );
              } else if (contentType === ChatAction.GetAddress) {
                return (
                  <CollectionForm
                    messageId={props.item.messageId}
                    fields={props.item.content.collectedFields}
                    onActionClick={this.handleActionClick.bind(this)}
                  />
                );
              } else if (contentType === ChatAction.ShowImage) {
                return <ImageAttachment src={content.imgUrl} alt='image from agent' />;
              } else {
                return <div></div>;
              }
            }}
            messageBox={showMessageSectionDisabled ? MessageSectionDisabled : MessageSection}
          />
          {showModal && (
            <Modal>
              <ChatExit
                onCancel={() => this.setState({ showModal: false })}
                onExit={this.handleEndChat}
                options={this.props.options}
              />
            </Modal>
          )}
          {showClientRouterModal && (
            <Modal>
              <ClientRouterModal
                clientRouterKey={this.props.options.apiKey}
                chatKey={this.state.routerChatKey}
                setChatKey={(chatKey) => this.setState({ routerChatKey: chatKey })}
                startChat={() => this.startChat()}
              />
            </Modal>
          )}
        </ChatBody>
        {showCSATSurvey && (
          <CSATSurvey
            botEnabled={this.injected.chatStore.botEnabled}
            onSkip={this.handleSkipSurvey}
            onSubmit={this.handleSubmitSurvey}
            surveyError={this.state.surveyError}
          />
        )}
        {status === ChannelStatus.Ended && !this.state.showCSATSurvey && (
          <div
            style={{
              color: primaryAccentColor ? primaryAccentColor : 'gray',
              backgroundColor: 'white',
              fontSize: '14px',
              fontWeight: 700,
              padding: 10,
              position: 'absolute',
              bottom: 0,
              width: 'calc(100% - 20px)',
              height: '42px',
            }}
          >
            <FlexContainer>
              <Button onClick={() => this.handleChatRestart()} filled style={{ width: '100%' }} data-testid='new-chat'>
                New Chat
              </Button>
            </FlexContainer>
          </div>
        )}
      </Container>
    );
  }

  private handleSubmitSurvey = async (surveyOptions: {
    botRating: BotResponseRating;
    comment: string;
    overallRating: CSATRating;
  }) => {
    this.setState({ surveyError: null });

    const { channelSid } = this.injected.chatStore;
    const res = await submitSurvey({ channelId: channelSid, ...surveyOptions });
    const errorMessage =
      res.errors && res.errors.length
        ? 'There was an issue submitting your response.'
        : res.data &&
          res.data.addInteractionCSATByChannelId &&
          !res.data.addInteractionCSATByChannelId.interaction &&
          res.data.addInteractionCSATByChannelId.error
        ? res.data.addInteractionCSATByChannelId.error.message
        : '';

    if (errorMessage) {
      this.setState({ surveyError: errorMessage });
    } else {
      if (this.state.isCustomerEndingChat) {
        this.chatExit();
      } else {
        this.setState({ showCSATSurvey: false });
      }
    }
  };

  private handleEndChat = () => {
    this.injected.chatStore.end();
    this.setState({ isCustomerEndingChat: true, showModal: false });
  };

  private handleSkipSurvey = () => {
    if (this.state.isCustomerEndingChat) {
      this.chatExit();
    } else {
      this.setState({ showCSATSurvey: false });
    }
  };

  private handleChatRestart = () => {
    // in the case of the client router, just close the chat store and show the router.
    // the router will handle restarting the chat.

    const { isRouterClient } = this.state;
    const { close, restart } = this.injected.chatStore;

    if (isRouterClient) {
      this.setState({ showClientRouterModal: true });
      close();
    } else {
      this.setState({ showCSATSurvey: true });
      restart();
    }
  };

  private async handleActionClick(message: string, messageId: string, removeAttachmment?: boolean | undefined) {
    const { chatStore } = this.injected;
    if (chatStore.messages.length === 1) {
      await chatStore.sendMessageWithGreeting(message, messageId, removeAttachmment);
    } else {
      await chatStore.sendSimpleMessage(message, messageId, removeAttachmment);
    }
  }

  private scrollMessageList() {
    setTimeout(() => {
      const messageListElement = document.getElementsByClassName('k-message-list')[0];
      if (messageListElement) {
        messageListElement.scrollTop = messageListElement.scrollHeight;
      }
    }, 500);
  }

  private minimize = () => {
    postMessage(CrossFrameMessageType.Minimize);
  };

  private chatExit = () => {
    this.minimize();
    this.injected.chatStore.restart();
    this.setState({
      showCSATSurvey: false, // we can do this since we're closing the chat anyway
      showModal: false,
    });
  };
}

export default withTheme(ChatArea);
